import { fetchCustomSchemaDefinition } from '@/composables/custom-data/customData';
import { NestApiType, nestCall } from '@/composables/nestApi';
import type {
  ImportAdapter,
  ImportContext,
  FieldDefinition,
  ImportWizardState,
  InitImportResponse,
} from '../types';
import { createAdapter } from './base.adapter';

// =============================================================================
// CUSTOM DATA IMPORT ADAPTER
// =============================================================================

interface CustomDataField {
  name: string;
  type: string;
  label?: string;
  required?: boolean;
  description?: string;
}

/**
 * Get fields from custom data schema definition
 */
async function getCustomDataFields(ctx: ImportContext): Promise<FieldDefinition[]> {
  const idCustomData = ctx.params?.idCustomData as number | undefined;

  if (!idCustomData) {
    throw new Error('ID of custom data is missing');
  }

  const response = await fetchCustomSchemaDefinition(idCustomData);

  if (!response) {
    throw new Error('No response from server');
  }

  if (!response.success) {
    throw new Error('Failed to fetch custom data fields');
  }

  if (!response.data) {
    throw new Error('No data received from server');
  }

  const fields: CustomDataField[] = response.data.fields || [];

  if (fields.length === 0) {
    throw new Error('No fields found in schema');
  }

  return fields.map((field) => ({
    name: field.name,
    type: field.type,
    // Use label if present and not empty, otherwise use name
    label: field.label && field.label.trim() !== '' ? field.label : field.name,
    required: field.required || false,
    description: field.description,
    // Store type for hint display
    formatHint: `import.customData.types.${field.type}`,
  }));
}

/**
 * Initialize custom data import
 */
async function initCustomDataImport(
  ctx: ImportContext,
  state: ImportWizardState,
  fileType: string,
): Promise<InitImportResponse> {
  const idCustomData = ctx.params?.idCustomData;

  const initData = {
    id_custom_data_definition: idCustomData,
    file_type: fileType,
    field_mappings: state.mapping,
  };

  const response = await nestCall(
    'post',
    NestApiType.CLIENT_API,
    '/job/import/init',
    {
      'import-type': 'custom_data',
    },
    '',
    initData,
  );

  if (response?.error) {
    throw new Error(response.error.message || 'Failed to initialize import');
  }

  if (!response?.data) {
    throw new Error('Invalid response from server');
  }

  return response.data;
}

/**
 * Custom Data Import Adapter
 */
export const customDataAdapter: ImportAdapter = createAdapter({
  type: 'custom_data',

  steps: [
    {
      id: 'file_select',
      type: 'file_select' as const,
      descriptionKey: 'import.wizard.steps.select',
      allowCopyPaste: true,
    },
    {
      id: 'column_mapping',
      type: 'column_mapping' as const,
      descriptionKey: 'import.wizard.steps.map',
    },
    {
      id: 'preview',
      type: 'preview' as const,
      descriptionKey: 'import.wizard.steps.preview',
    },
  ],

  getFields: getCustomDataFields,

  validateMapping: () => true,

  initImport: initCustomDataImport,

  getPreviewData: (state: ImportWizardState) => ({
    totalRows: state.parsedFile?.rows?.length ?? 0,
    mappedFields: state.mapping.length,
    fileName: state.fileData.file?.name ?? 'pasted-data.csv',
  }),
});

export default customDataAdapter;
